import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Users,
  TrendingUp,
  Target,
  Store,
  AlertTriangle,
  ArrowUpRight,
  Phone,
  Calendar,
  CalendarDays,
  Filter,
  UserX,
  Clock,
  CheckCircle2,
  Video,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, FunnelChart, Funnel, LabelList } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const LEAD_STAGES = [
  'New Lead',
  'Uncontacted',
  'Qualified Lead',
  'Virtual Meet',
  'Awaited Visit',
  'Awaited LOI',
  'Closed LOI',
  'Outlet Opened',
  'Nurture',
  'Dead'
];

const DashboardPage = () => {
  const { api, isManager, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [showDailySummary, setShowDailySummary] = useState(false);
  const [funnelData, setFunnelData] = useState(null);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Team filter state
  const [teamFilter, setTeamFilter] = useState('all');
  
  // Lifecycle filter (60 days)
  const [lifecycleFilter, setLifecycleFilter] = useState(false);
  
  // Conversion comparison state
  const [fromStage, setFromStage] = useState('');
  const [toStage, setToStage] = useState('');
  const [conversionResult, setConversionResult] = useState(null);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => new Date().toISOString().split('T')[0];
  
  // Get date ranges for quick filters
  const getDateRange = (filter) => {
    const today = new Date();
    let start, end;
    
    switch (filter) {
      case 'today':
        start = end = getTodayDate();
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        start = weekStart.toISOString().split('T')[0];
        end = getTodayDate();
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = getTodayDate();
        break;
      default:
        start = '';
        end = '';
    }
    
    return { start, end };
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    const { start, end } = getDateRange(filter);
    setStartDate(start);
    setEndDate(end);
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (teamFilter && teamFilter !== 'all') params.append('team', teamFilter);
      if (lifecycleFilter) params.append('lifecycle_days', '60');
      
      const [statsRes, leadsRes] = await Promise.all([
        api.get(`/dashboard/stats?${params.toString()}`),
        api.get('/dashboard/recent-leads?limit=5'),
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data);
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [api, startDate, endDate, teamFilter, lifecycleFilter]);

  const fetchExecutiveSummary = useCallback(async () => {
    try {
      const response = await api.get('/dashboard/executive-summary');
      setExecutiveSummary(response.data);
      
      // Show daily summary popup on first login of the day
      const lastShownDate = localStorage.getItem('lastDailySummaryDate');
      const today = getTodayDate();
      if (lastShownDate !== today) {
        setShowDailySummary(true);
        localStorage.setItem('lastDailySummaryDate', today);
      }
    } catch (err) {
      console.error('Failed to load executive summary');
    }
  }, [api]);

  const fetchFunnelData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await api.get(`/dashboard/conversion-funnel?${params.toString()}`);
      setFunnelData(response.data);
    } catch (err) {
      console.error('Failed to load funnel data');
    }
  }, [api, startDate, endDate]);

  const fetchStageConversion = async () => {
    if (!fromStage || !toStage) {
      toast.error('Please select both stages');
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append('from_stage', fromStage);
      params.append('to_stage', toStage);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await api.get(`/dashboard/conversion-funnel?${params.toString()}`);
      setConversionResult(response.data);
    } catch (err) {
      toast.error('Failed to calculate conversion');
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchExecutiveSummary();
    fetchFunnelData();
  }, [fetchDashboardData, fetchExecutiveSummary, fetchFunnelData]);

  // Auto-refresh dashboard every 30 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchDashboardData();
      fetchFunnelData();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [fetchDashboardData, fetchFunnelData]);

  const getStatusColor = (status) => {
    const colors = {
      'New Lead': 'bg-blue-100 text-blue-800',
      'Uncontacted': 'bg-gray-100 text-gray-800',
      'Qualified Lead': 'bg-indigo-100 text-indigo-800',
      'Virtual Meet': 'bg-purple-100 text-purple-800',
      'Awaited Visit': 'bg-orange-100 text-orange-800',
      'Awaited LOI': 'bg-yellow-100 text-yellow-800',
      'Closed LOI': 'bg-teal-100 text-teal-800',
      'Outlet Opened': 'bg-green-100 text-green-800',
      'Nurture': 'bg-cyan-100 text-cyan-800',
      'Dead': 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const CHART_COLORS = ['#E31E24', '#FF6B6B', '#FF8E8E', '#FFA5A5', '#FFB8B8', '#FFCECE', '#ec4899', '#f43f5e', '#ef4444', '#14b8a6'];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  // Prepare chart data
  const statusData = stats?.statusCounts
    ? Object.entries(stats.statusCounts).map(([status, count]) => ({
        name: status.replace(/([A-Z])/g, ' $1').trim(),
        value: count,
      }))
    : [];

  const executiveData = stats?.executiveStats || [];

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Daily Summary Modal */}
      <Dialog open={showDailySummary} onOpenChange={setShowDailySummary}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              Good Morning, {user?.name?.split(' ')[0]}!
            </DialogTitle>
            <DialogDescription>
              Here's your daily summary for {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </DialogDescription>
          </DialogHeader>
          
          {executiveSummary && (
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <Video className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                <p className="text-2xl font-bold text-purple-700">{executiveSummary.summary.meetingsToday}</p>
                <p className="text-sm text-purple-600">Meetings Today</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <Phone className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <p className="text-2xl font-bold text-blue-700">{executiveSummary.summary.callsToMake}</p>
                <p className="text-sm text-blue-600">Calls to Make</p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg text-center">
                <Clock className="h-6 w-6 mx-auto mb-2 text-orange-600" />
                <p className="text-2xl font-bold text-orange-700">{executiveSummary.summary.pendingFollowups}</p>
                <p className="text-sm text-orange-600">Pending Follow-ups</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-green-600" />
                <p className="text-2xl font-bold text-green-700">{executiveSummary.summary.updatedToday}</p>
                <p className="text-sm text-green-600">Updated Today</p>
              </div>
            </div>
          )}
          
          <div className="flex justify-end">
            <Button onClick={() => setShowDailySummary(false)}>
              Let's Get Started!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header with Date Filter */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground">
            Here&apos;s what&apos;s happening with your leads.
          </p>
        </div>
        
        {/* Date Filter Controls */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Team Filter - Only for Managers */}
          {isManager && (
            <div className="flex items-center gap-2">
              <Select value={teamFilter} onValueChange={setTeamFilter}>
                <SelectTrigger className="w-[130px]" data-testid="team-filter">
                  <SelectValue placeholder="All Teams" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  <SelectItem value="Servall">Servall Team</SelectItem>
                  <SelectItem value="R2G">R2G Team</SelectItem>
                  <SelectItem value="Shivaay">Shivaay Team</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* 60-Day Lifecycle Toggle */}
          <Button 
            variant={lifecycleFilter ? "default" : "outline"} 
            size="sm"
            onClick={() => {
              setLifecycleFilter(!lifecycleFilter);
              if (!lifecycleFilter) {
                setDateFilter('all');
                setStartDate('');
                setEndDate('');
              }
            }}
            data-testid="lifecycle-filter"
          >
            <Calendar className="h-4 w-4 mr-1" />
            60 Days
          </Button>
          
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={dateFilter} onValueChange={handleDateFilterChange} disabled={lifecycleFilter}>
              <SelectTrigger className="w-[130px]" data-testid="date-filter">
                <SelectValue placeholder="All Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {dateFilter === 'custom' && !lifecycleFilter && (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-[140px]"
                data-testid="start-date"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-[140px]"
                data-testid="end-date"
              />
            </div>
          )}
          
          <Button onClick={() => navigate('/leads-board')} data-testid="view-leads-btn">
            View All Leads
            <ArrowUpRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Executive Daily Summary Section (Always Visible) */}
      {executiveSummary && (
        <Card className="border-primary/20 bg-primary/5" data-testid="daily-summary-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg font-['Manrope']">
              <CalendarDays className="h-5 w-5 text-primary" />
              Today's Priorities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="p-3 bg-white rounded-lg text-center shadow-sm">
                <p className="text-2xl font-bold text-purple-600">{executiveSummary.summary.meetingsToday}</p>
                <p className="text-xs text-muted-foreground">Meetings</p>
              </div>
              <div className="p-3 bg-white rounded-lg text-center shadow-sm">
                <p className="text-2xl font-bold text-blue-600">{executiveSummary.summary.callsToMake}</p>
                <p className="text-xs text-muted-foreground">Calls to Make</p>
              </div>
              <div className="p-3 bg-white rounded-lg text-center shadow-sm">
                <p className="text-2xl font-bold text-orange-600">{executiveSummary.summary.pendingFollowups}</p>
                <p className="text-xs text-muted-foreground">Follow-ups</p>
              </div>
              <div className="p-3 bg-white rounded-lg text-center shadow-sm">
                <p className="text-2xl font-bold text-teal-600">{executiveSummary.summary.awaitedVisits}</p>
                <p className="text-xs text-muted-foreground">Awaited Visits</p>
              </div>
              <div className="p-3 bg-white rounded-lg text-center shadow-sm">
                <p className="text-2xl font-bold text-cyan-600">{executiveSummary.summary.nurtureLeads}</p>
                <p className="text-xs text-muted-foreground">In Nurture</p>
              </div>
              <div className="p-3 bg-white rounded-lg text-center shadow-sm">
                <p className="text-2xl font-bold text-green-600">{executiveSummary.summary.totalAssigned}</p>
                <p className="text-xs text-muted-foreground">Total Assigned</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="card-hover" data-testid="total-leads-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Leads
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalLeads || 0}</div>
            <p className="text-xs text-muted-foreground">
              +{stats?.newThisWeek || 0} this week
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="conversion-rate-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Conversion Rate
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.conversionRate || 0}%</div>
            <p className="text-xs text-muted-foreground">
              Outlets / Total
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="qualified-leads-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Qualified Leads
            </CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.qualifiedLeads || 0}</div>
            <p className="text-xs text-muted-foreground">
              Ready for conversion
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="outlets-opened-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Outlets Opened
            </CardTitle>
            <Store className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.outletsOpened || 0}</div>
            <p className="text-xs text-muted-foreground">
              Successfully converted
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover border-orange-200 bg-orange-50/50" data-testid="unassigned-leads-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-orange-700">
              Unassigned
            </CardTitle>
            <UserX className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-700">{stats?.unassignedLeads || 0}</div>
            <p className="text-xs text-orange-600">
              Need assignment
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Stage-wise Conversion Section */}
      {isManager && (
        <Card data-testid="conversion-funnel-card">
          <CardHeader>
            <CardTitle className="font-['Manrope']">Sales Funnel Analytics</CardTitle>
            <CardDescription>
              Cumulative conversion ratios and stage comparison
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Cumulative Ratios Display */}
            {funnelData?.cumulativeRatios && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6 p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg">
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-blue-600">{funnelData.cumulativeRatios.qualificationRatio}%</p>
                  <p className="text-xs text-muted-foreground">Qualification Ratio</p>
                  <p className="text-xs text-blue-500 mt-1">{funnelData.cumulativeRatios.qualificationCount} leads</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-purple-600">{funnelData.cumulativeRatios.meetRatio}%</p>
                  <p className="text-xs text-muted-foreground">Meet Ratio</p>
                  <p className="text-xs text-purple-500 mt-1">{funnelData.cumulativeRatios.meetCount} leads</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-green-600">{funnelData.cumulativeRatios.loiRatio}%</p>
                  <p className="text-xs text-muted-foreground">LOI Ratio</p>
                  <p className="text-xs text-green-500 mt-1">{funnelData.cumulativeRatios.loiCount} leads</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-red-600">{funnelData.cumulativeRatios.deadRatio}%</p>
                  <p className="text-xs text-muted-foreground">Dead Ratio</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-cyan-600">{funnelData.cumulativeRatios.coolDownRatio}%</p>
                  <p className="text-xs text-muted-foreground">Cool Down Ratio</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Stage Comparison Tool */}
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="flex-1 w-full">
                    <Label className="text-xs text-muted-foreground mb-1 block">From Stage</Label>
                    <Select value={fromStage} onValueChange={setFromStage}>
                      <SelectTrigger data-testid="from-stage-select">
                        <SelectValue placeholder="Select stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {LEAD_STAGES.map((stage) => (
                          <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground hidden sm:block mt-5" />
                  <div className="flex-1 w-full">
                    <Label className="text-xs text-muted-foreground mb-1 block">To Stage</Label>
                    <Select value={toStage} onValueChange={setToStage}>
                      <SelectTrigger data-testid="to-stage-select">
                        <SelectValue placeholder="Select stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {LEAD_STAGES.map((stage) => (
                          <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={fetchStageConversion} className="mt-5" data-testid="calculate-conversion-btn">
                    Calculate
                  </Button>
                </div>
                
                {conversionResult && (
                  <div className="p-4 bg-muted rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">{conversionResult.fromStage}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{conversionResult.toStage}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{conversionResult.fromCount}</p>
                        <p className="text-xs text-muted-foreground">From Stage</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-600">{conversionResult.toCount}</p>
                        <p className="text-xs text-muted-foreground">To Stage</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-primary">{conversionResult.conversionRate}%</p>
                        <p className="text-xs text-muted-foreground">Conversion</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Funnel Visualization */}
              <div>
                {funnelData && (
                  <div className="space-y-2">
                    {funnelData.funnelData?.filter(d => !['Dead', 'Nurture'].includes(d.stage)).map((item, index) => (
                      <div key={item.stage} className="flex items-center gap-3">
                        <div className="w-24 text-xs text-right truncate">{item.stage}</div>
                        <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all"
                            style={{
                              width: `${funnelData.totalLeads > 0 ? (item.count / funnelData.totalLeads) * 100 : 0}%`,
                              opacity: 1 - (index * 0.08)
                            }}
                          />
                        </div>
                        <div className="w-12 text-sm font-medium">{item.count}</div>
                        {item.conversionFromPrevious > 0 && index > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {item.conversionFromPrevious}%
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      {isManager && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Lead Status Distribution */}
          <Card data-testid="status-chart-card">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Lead Status Distribution</CardTitle>
              <CardDescription>
                Breakdown of leads by current status
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]" style={{ minHeight: '300px', width: '100%' }}>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusData.filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-2 mt-4 justify-center">
                {statusData.filter(d => d.value > 0).map((entry, index) => (
                  <div key={entry.name} className="flex items-center gap-2 text-sm">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                    />
                    <span className="text-muted-foreground">{entry.name}: {entry.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Executive Performance */}
          <Card data-testid="executive-chart-card">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Executive Performance</CardTitle>
              <CardDescription>
                Leads managed by each executive {teamFilter !== 'all' && `(${teamFilter} Team)`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]" style={{ minHeight: '300px', width: '100%' }}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={executiveData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="name" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="totalLeads" fill="#E31E24" name="Total Leads" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="outletsOpened" fill="#22c55e" name="Outlets Opened" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team Stats Section */}
      {isManager && stats?.teamStats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {stats.teamStats.map((team) => (
            <Card 
              key={team.team} 
              className={`card-hover cursor-pointer ${teamFilter === team.team ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setTeamFilter(teamFilter === team.team ? 'all' : team.team)}
              data-testid={`team-card-${team.team.toLowerCase()}`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-['Manrope'] flex items-center justify-between">
                  <span>{team.team} Team</span>
                  <Badge variant={teamFilter === team.team ? 'default' : 'outline'}>
                    {team.userCount} Users
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{team.leadCount}</p>
                    <p className="text-xs text-muted-foreground">Leads</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{team.convertedLeads}</p>
                    <p className="text-xs text-muted-foreground">Converted</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">
                      {team.leadCount > 0 ? ((team.convertedLeads / team.leadCount) * 100).toFixed(1) : 0}%
                    </p>
                    <p className="text-xs text-muted-foreground">Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* City-wise Charts */}
      {isManager && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* City Distribution - Total Leads */}
          <Card data-testid="city-chart-card">
            <CardHeader>
              <CardTitle className="font-['Manrope']">City-wise Lead Distribution</CardTitle>
              <CardDescription>Top 15 cities by lead count</CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.cityDistribution && stats.cityDistribution.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.cityDistribution} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                      <YAxis 
                        type="category" 
                        dataKey="city" 
                        width={100}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} 
                      />
                      <Tooltip />
                      <Bar dataKey="count" fill="#E31E24" name="Leads" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No city data available</p>
              )}
            </CardContent>
          </Card>

          {/* City Distribution - Awaiting LOI */}
          <Card data-testid="city-loi-chart-card">
            <CardHeader>
              <CardTitle className="font-['Manrope']">City-wise Awaiting LOI</CardTitle>
              <CardDescription>Leads awaiting LOI by city</CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.cityLoiDistribution && stats.cityLoiDistribution.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.cityLoiDistribution} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                      <YAxis 
                        type="category" 
                        dataKey="city" 
                        width={100}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} 
                      />
                      <Tooltip />
                      <Bar dataKey="count" fill="#f59e0b" name="Awaiting LOI" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No LOI data available</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <Card data-testid="recent-leads-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-['Manrope']">Recent Leads</CardTitle>
              <CardDescription>Latest leads added to the system</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/leads-board')}>
              View all
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentLeads.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No leads yet
                </p>
              ) : (
                recentLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{lead.name}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="h-3 w-3" />
                        {lead.mobile}
                      </div>
                    </div>
                    <Badge className={getStatusColor(lead.status)}>
                      {lead.status}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Inactive Leads Alert (Manager Only) */}
        {isManager && stats?.inactiveLeads?.length > 0 && (
          <Card className="border-orange-200 bg-orange-50/50" data-testid="inactive-leads-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                <CardTitle className="font-['Manrope'] text-orange-700">
                  Inactive Leads Alert
                </CardTitle>
              </div>
              <CardDescription className="text-orange-600">
                These leads haven&apos;t been updated in over 15 days
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.inactiveLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-white border border-orange-100"
                  >
                    <div>
                      <p className="font-medium">{lead.name}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        Last updated: {new Date(lead.updatedAt).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-orange-600 border-orange-300">
                      {lead.assignedExecutiveName || 'Unassigned'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lead Sources (Manager Only) */}
        {isManager && stats?.leadSources?.length > 0 && !stats?.inactiveLeads?.length && (
          <Card data-testid="lead-sources-card">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Lead Sources</CardTitle>
              <CardDescription>Where your leads are coming from</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.leadSources.slice(0, 5).map((source, index) => (
                  <div key={source.source} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{source.source}</span>
                        <span className="text-sm text-muted-foreground">{source.count}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{
                            width: `${(source.count / stats.totalLeads) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
